import { FC } from 'react';
import { Typography } from '@mui/material';

import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { getCopy } from 'ui-component/DeleteConfirmationDialog/copy';
import useHandleDelete from 'ui-component/DeleteConfirmationDialog/useHandleDelete';
import { ProductionRun } from 'types/production';
import { ManagementActionTypes } from 'types';
import { BomCRUDSchema } from 'types/bom';
import { appPermissionAccessLevels } from 'types/apps';
import { ALL_APPS } from 'constants/appConstants';

interface DeleteConfirmationDialogProps {
  idToDelete: BomCRUDSchema['id'] | ProductionRun['id'];
  dialogOpen: boolean;
  onClose: () => void;
  actionType: Exclude<keyof typeof ManagementActionTypes, 'team'>;
  navigate?: () => void;
  message?: string;
}

/**
 * Generalized confirmation dialog to handle deleting an object from the database.
 * Leverages a custom hook to call correct endpoint based on actionType prop.
 */
const DeleteConfirmationDialog: FC<DeleteConfirmationDialogProps> = ({
  idToDelete,
  dialogOpen,
  onClose,
  actionType,
  navigate,
  message,
}) => {
  const copy = getCopy();

  const onSuccess = () => {
    handleClose();
    if (navigate) {
      navigate();
    }
  };

  const { handleDelete, isLoading, error, resetError } = useHandleDelete({
    id: idToDelete,
    actionType,
    onSuccess,
  });

  const handleClose = () => {
    resetError();
    onClose();
  };

  const appIds = {
    [ManagementActionTypes.bom]: ALL_APPS.BOMS.id,
    [ManagementActionTypes.productionRun]: ALL_APPS.PRODUCTION.id,
    [ManagementActionTypes.supplier]: ALL_APPS.SUPPLIERS.id,
    [ManagementActionTypes.purchaseRule]: ALL_APPS.SUPPLIERS.id,
    [ManagementActionTypes.purchaseOrder]: ALL_APPS.PROCUREMENT.id,
  };

  return (
    <ExtendedDialog
      onCloseDialog={handleClose}
      open={dialogOpen}
      fullWidth
      maxWidth="sm"
      title={copy[actionType].title}
      isForm
      onSubmit={handleDelete}
      isSubmitting={isLoading}
      submitButtonCopy="Delete"
      submitButtonColor="error"
      formSubmitError={error}
      permissionScope={{
        app: appIds[actionType],
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      <Typography>{copy[actionType].body}</Typography>
      {!!message && <Typography>{message}</Typography>}
    </ExtendedDialog>
  );
};

export default DeleteConfirmationDialog;
